import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Scissors } from 'lucide-react';
import { RangeSlider } from './RangeSlider';

// Shared with EditorInspectorPanel for sub-second-precise readouts (e.g.
// transform/trim values) — mm:ss.s, distinct from EditorTimelineRuler's
// mm:ss used for whole-timeline clocks.
export const fmtTimePrecise = (s) => {
  const m = Math.floor(s / 60);
  const sec = (s % 60).toFixed(1);
  return `${m}:${sec.padStart(4, '0')}`;
};

/**
 * Trim handles for the selected clip, reusing the Radix range slider plus
 * numeric inputs for precise entry, and a "Split at Playhead" action.
 */
export function EditorClipTrimControls({ clip, duration, currentTime, onChange, onSplit }) {
  if (!clip) {
    return (
      <p className="text-sm text-[#8A93A6] text-center py-6" data-testid="trim-controls-empty">
        Select or add a clip to edit its trim range.
      </p>
    );
  }

  const canSplit = currentTime > clip.in_point + 0.2 && currentTime < clip.out_point - 0.2;

  const handleRangeChange = ([start, end]) => {
    onChange({ in_point: start, out_point: end });
  };

  return (
    <div className="space-y-3" data-testid="trim-controls">
      <RangeSlider
        value={[clip.in_point, clip.out_point]}
        min={0}
        max={duration || 1}
        step={0.1}
        onValueChange={handleRangeChange}
        data-testid="trim-range-slider"
      />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-[#8A93A6] mb-1 block">Start</label>
          <Input
            type="number" step="0.1" value={clip.in_point.toFixed(1)}
            onChange={(e) => onChange({ in_point: Math.min(Number(e.target.value), clip.out_point - 0.2) })}
            className="bg-[#1C1F26] border-white/10 text-white" data-testid="trim-start-input"
          />
        </div>
        <div>
          <label className="text-xs text-[#8A93A6] mb-1 block">End</label>
          <Input
            type="number" step="0.1" value={clip.out_point.toFixed(1)}
            onChange={(e) => onChange({ out_point: Math.max(Number(e.target.value), clip.in_point + 0.2) })}
            className="bg-[#1C1F26] border-white/10 text-white" data-testid="trim-end-input"
          />
        </div>
      </div>
      <p className="text-xs text-[#8A93A6]">Duration: {fmtTimePrecise(clip.out_point - clip.in_point)}</p>
      <Button
        variant="outline" size="sm" disabled={!canSplit} onClick={onSplit}
        className="w-full border-white/10" data-testid="split-at-playhead-button"
      >
        <Scissors className="h-4 w-4 mr-2" />
        Split at Playhead ({fmtTimePrecise(currentTime)})
      </Button>
    </div>
  );
}
